// import { TranslateResult } from 'vue-i18n'
import { localI18n } from '@/plugins/i18n'
import DOMPurify from 'dompurify'
import { marked } from 'marked'
import {
  // COLUMN_GEO_TYPE,
  COLUMN_TYPE
} from '@locokit/lck-glossary'
import type {
  LCKTableRowMultiDataComplex,
  LckAttachment,
  LckTableColumn,
  LckTableRowData,
  LckTableRowDataComplex,
  LckTableRowOffline,
  LckTableViewColumn,
  SelectValue
} from '../lck-api/definitions'
import { formatDate } from './date'

// import {
//   LckTableColumn,
//   type LckTableRowData,
//   LckTableRowDataComplex,
//   LCKTableRowMultiDataComplex,
//   LckTableViewColumn,
//   type SelectValue,
// } from '@/services/lck-api/definitions'
// import { formatDate } from '@/services/lck-utils/date'
// import { type PopupContent } from './map/transformWithOL'
import { type TranslateResult } from 'vue-i18n'

/**
 * DataTable cells display & editor components
 */

/**
 * This function return editor component of column type, for DataTable cell use
 * @param columnTypeId ColumnType id
 * @returns component's name
 */
export function getComponentEditorCellForColumnType(columnTypeId: number) {
  switch (columnTypeId) {
    case COLUMN_TYPE.USER:
    case COLUMN_TYPE.GROUP:
    case COLUMN_TYPE.RELATION_BETWEEN_TABLES:
      return 'lck-autocomplete'
    case COLUMN_TYPE.MULTI_USER:
    case COLUMN_TYPE.MULTI_GROUP:
      return 'lck-multi-autocomplete'
    case COLUMN_TYPE.NUMBER:
      return 'p-input-number'
    case COLUMN_TYPE.FLOAT:
      return 'p-input-float' // not a real component
    case COLUMN_TYPE.MULTI_SELECT:
      return 'multiselect'
    case COLUMN_TYPE.SINGLE_SELECT:
      return 'p-dropdown'
    case COLUMN_TYPE.DATE:
      return 'p-calendar'
    case COLUMN_TYPE.DATETIME:
      return 'p-calendar-time'
    case COLUMN_TYPE.STRING:
      return 'p-input-text'
    case COLUMN_TYPE.TEXT:
      return 'p-textarea'
    case COLUMN_TYPE.URL:
      return 'lck-url-input'
    case COLUMN_TYPE.BOOLEAN:
      return 'p-checkbox'
    default:
      return null
  }
}

/**
 * This function return display component of column type, for DataTable cell use
 * @param columnTypeId ColumnType id
 * @returns component's name
 */
export function getComponentDisplayCellForColumnType(columnTypeId: number) {
  switch (columnTypeId) {
    case COLUMN_TYPE.SINGLE_SELECT:
      return 'lck-badge'
    case COLUMN_TYPE.MULTI_SELECT:
      return 'lck-multi-badges'
    case COLUMN_TYPE.BOOLEAN:
      return 'p-checkbox'
    case COLUMN_TYPE.FILE:
      return 'data-field-file'
    default:
      return null
  }
}

/**
 * DataDetail display & editor components
 */

/**
 * This function return editor component of column type, for DataDetail use
 * @param columnTypeId ColumnType id
 * @returns component's name
 */
export function getComponentEditorDetailForColumnType(columnTypeId: number) {
  switch (columnTypeId) {
    case COLUMN_TYPE.USER:
    case COLUMN_TYPE.GROUP:
    case COLUMN_TYPE.RELATION_BETWEEN_TABLES:
      return 'lck-autocomplete'
    case COLUMN_TYPE.MULTI_USER:
    case COLUMN_TYPE.MULTI_GROUP:
      return 'lck-multi-autocomplete'
    case COLUMN_TYPE.NUMBER:
      return 'prime-input-number'
    case COLUMN_TYPE.FLOAT:
      return 'prime-input-float' // not a real component
    case COLUMN_TYPE.MULTI_SELECT:
      return 'lck-multiselect'
    case COLUMN_TYPE.SINGLE_SELECT:
      return 'prime-select'
    case COLUMN_TYPE.DATE:
      return 'prime-calendar'
    case COLUMN_TYPE.DATETIME:
      return 'prime-calendar-time'
    case COLUMN_TYPE.STRING:
      return 'prime-input-text'
    case COLUMN_TYPE.TEXT:
      return 'prime-textarea'
    case COLUMN_TYPE.URL:
      return 'lck-url-input'
    case COLUMN_TYPE.BOOLEAN:
      return 'prime-checkbox'
    case COLUMN_TYPE.GEOMETRY_POINT:
    case COLUMN_TYPE.GEOMETRY_LINESTRING:
    case COLUMN_TYPE.GEOMETRY_POLYGON:
    case COLUMN_TYPE.GEOMETRY_MULTIPOINT:
    case COLUMN_TYPE.GEOMETRY_MULTILINESTRING:
    case COLUMN_TYPE.GEOMETRY_MULTIPOLYGON:
      return 'lck-map'
    case COLUMN_TYPE.FILE:
      return 'lck-file-input'
    default:
      return null
  }
}

/**
 * Check if a column / field is displayed
 * * check its displayed property
 * * check also conditions to display this field
 */
export function isFieldDisplayed(
  field: LckTableViewColumn,
  fields: Record<
    string,
    {
      column_type_id: COLUMN_TYPE
    }
  > = {},
  data: Record<string, LckTableRowData> = {}
) {
  let isDisplayed =
    field.displayed === null ||
    field.displayed === undefined ||
    field.displayed === true
  const geometryTypes = [
    COLUMN_TYPE.GEOMETRY_LINESTRING,
    COLUMN_TYPE.GEOMETRY_MULTILINESTRING,
    COLUMN_TYPE.GEOMETRY_POINT,
    COLUMN_TYPE.GEOMETRY_POLYGON,
    COLUMN_TYPE.GEOMETRY_MULTIPOINT,
    COLUMN_TYPE.GEOMETRY_MULTIPOLYGON
  ]
  // do not display geometry fields, on this project specifically
  if (geometryTypes.includes(field.column_type_id)) return false
  // return early to avoid useless computation if it's not displayed
  if (!isDisplayed) return isDisplayed

  field.display_conditions?.forEach(({ field_id, operator, value }) => {
    let fieldValue: LckTableRowData | undefined = data[field_id]
    switch (fields[field_id]?.column_type_id) {
      case COLUMN_TYPE.USER:
      case COLUMN_TYPE.GROUP:
      case COLUMN_TYPE.RELATION_BETWEEN_TABLES:
        fieldValue = (data[field_id] as LckTableRowDataComplex)?.reference
        break
      /*
      case COLUMN_TYPE.BOOLEAN:
      case COLUMN_TYPE.STRING:
      case COLUMN_TYPE.NUMBER:
      case COLUMN_TYPE.FLOAT:
      case COLUMN_TYPE.DATE:
      case COLUMN_TYPE.SINGLE_SELECT:
      case COLUMN_TYPE.TEXT:
      case COLUMN_TYPE.URL:
      */
      default:
        break
    }
    switch (operator) {
      case '$eq':
        if (fieldValue !== value) isDisplayed = false
        break
      case '$ne':
        if (fieldValue === value) isDisplayed = false
        break
      case '$in':
        if (!(value as string[] | number[]).includes(fieldValue as never))
          isDisplayed = false
        break
    }
  })
  return isDisplayed
}

/**
 * This function return display component of column type, for DataDetail use
 * @param columnTypeId ColumnType id
 * @returns component's name
 */
export function getComponentDisplayDetailForColumnType(columnTypeId: number) {
  switch (columnTypeId) {
    case COLUMN_TYPE.SINGLE_SELECT:
      return 'lck-badge'
    case COLUMN_TYPE.MULTI_SELECT:
      return 'lck-multi-badges'
    case COLUMN_TYPE.BOOLEAN:
      return 'p-checkbox'
    case COLUMN_TYPE.GEOMETRY_POINT:
    case COLUMN_TYPE.GEOMETRY_LINESTRING:
    case COLUMN_TYPE.GEOMETRY_POLYGON:
    case COLUMN_TYPE.GEOMETRY_MULTIPOINT:
    case COLUMN_TYPE.GEOMETRY_MULTILINESTRING:
    case COLUMN_TYPE.GEOMETRY_MULTIPOLYGON:
      return 'lck-map'
    case COLUMN_TYPE.FILE:
      return 'lck-file-input'
    case COLUMN_TYPE.URL:
      return 'lck-url'
    default:
      return null
  }
}

export const READ_ONLY_COLUMNS_TYPES = new Set([
  COLUMN_TYPE.LOOKED_UP_COLUMN,
  COLUMN_TYPE.VIRTUAL_LOOKED_UP_COLUMN,
  COLUMN_TYPE.FORMULA
])
/**
 * Check if a column / field is editable
 *
 * @param column The column to check
 * @param crudMode Do we want to check we can use this column in a CRUD mode ? (need to be explained more)
 * @returns true if the column / field is editable
 */
export function isEditableColumn(
  column: LckTableViewColumn,
  crudMode: boolean
) {
  return (
    !READ_ONLY_COLUMNS_TYPES.has(column.column_type_id) &&
    (crudMode || column.editable)
  )
}

export function getColumnTypeId(column: LckTableColumn): COLUMN_TYPE {
  if (column.column_type_id === COLUMN_TYPE.FORMULA)
    return column?.settings?.formula_type_id as COLUMN_TYPE
  if (
    (column.column_type_id !== COLUMN_TYPE.LOOKED_UP_COLUMN &&
      column.column_type_id !== COLUMN_TYPE.VIRTUAL_LOOKED_UP_COLUMN) ||
    (column.parents && column.parents.length === 0) ||
    !column.parents
  ) {
    return column.column_type_id
  }
  return getColumnTypeId(column.parents[0])
}

export function getOriginalColumn(column: LckTableColumn): LckTableColumn {
  if (
    (column.column_type_id !== COLUMN_TYPE.LOOKED_UP_COLUMN &&
      column.column_type_id !== COLUMN_TYPE.VIRTUAL_LOOKED_UP_COLUMN) ||
    (column.parents && column.parents.length === 0) ||
    !column.parents
  ) {
    return column
  }
  return getOriginalColumn(column.parents[0])
}

// export function getColumnClass (column: LckTableViewColumn): string {
//   switch (column.column_type_id) {
//     case COLUMN_TYPE.BOOLEAN:
//       return 'bi bi-check2-square'
//     case COLUMN_TYPE.STRING:
//       return 'bi bi-type'
//     case COLUMN_TYPE.NUMBER:
//       return 'bi bi-hash'
//     case COLUMN_TYPE.FLOAT:
//       return 'bi bi-hash'
//     case COLUMN_TYPE.DATE:
//       return 'bi bi-calendar-date'
//     case COLUMN_TYPE.DATETIME:
//       return 'bi bi-clock'
//     case COLUMN_TYPE.USER:
//       return 'bi bi-person-fill'
//     case COLUMN_TYPE.GROUP:
//       return 'bi bi-people-fill'
//     case COLUMN_TYPE.RELATION_BETWEEN_TABLES:
//       return 'bi bi-text-indent-left'
//     case COLUMN_TYPE.LOOKED_UP_COLUMN:
//       return 'bi bi-binoculars-fill'
//     case COLUMN_TYPE.VIRTUAL_LOOKED_UP_COLUMN:
//       return 'bi bi-binoculars'
//     case COLUMN_TYPE.SINGLE_SELECT:
//       return 'bi bi-caret-down-square'
//     case COLUMN_TYPE.MULTI_SELECT:
//       return 'bi bi-list-check'
//     case COLUMN_TYPE.FORMULA:
//       return 'bi bi-magic'
//     case COLUMN_TYPE.FILE:
//       return 'bi bi-file-earmark'
//     case COLUMN_TYPE.MULTI_USER:
//       return 'bi bi-people-fill'
//     case COLUMN_TYPE.MULTI_GROUP:
//       return 'bi bi-people-fill'
//     case COLUMN_TYPE.TEXT:
//       return 'bi bi-textarea-t'
//     case COLUMN_TYPE.URL:
//       return 'bi bi-link-45deg'
//     case COLUMN_TYPE.GEOMETRY_POINT:
//       return 'bi bi-geo'
//     case COLUMN_TYPE.GEOMETRY_MULTIPOINT:
//       return 'bi bi-geo'
//     case COLUMN_TYPE.GEOMETRY_POLYGON:
//       return 'bi bi-bounding-box-circles'
//     case COLUMN_TYPE.GEOMETRY_MULTIPOLYGON:
//       return 'bi bi-bounding-box-circles'
//     case COLUMN_TYPE.GEOMETRY_LINESTRING:
//       return 'bi bi-slash-lg'
//     case COLUMN_TYPE.GEOMETRY_MULTILINESTRING:
//       return 'bi bi-slash-lg'
//     default:
//       return ''
//   }
// }

/**
 * Return a list of the geographic column types
 */
// export const GEOGRAPHIC_COLUMN_TYPES = Object.keys(COLUMN_GEO_TYPE).reduce((acc: number[], item: string) => {
//   const columnTypeId = parseInt(item)
//   if (!isNaN(columnTypeId)) acc.push(columnTypeId)
//   return acc
// }, [] as number[])

/**
 * Return the display value for a column.
 * By taking the backend data column value,
 * retrieve the good part of the data to be displayed.
 *
 * @param column
 * The column definition
 *
 * @param data
 * The data to be analyzed
 */
export function getColumnDisplayValue(
  column: LckTableColumn,
  data: LckTableRowData = '',
  onlyBaseValue = false,
  i18nOptions?: {
    dateFormat: string | TranslateResult
    dateTimeFormat: string | TranslateResult
  }
): SelectValue | string | undefined | SelectValue[] | LckAttachment[] {
  if (data === '' || data === undefined || data === null) {
    if (column.column_type_id === COLUMN_TYPE.MULTI_SELECT && !onlyBaseValue)
      return []
    return ''
  }
  try {
    switch (column.column_type_id) {
      case COLUMN_TYPE.USER:
      case COLUMN_TYPE.GROUP:
      case COLUMN_TYPE.RELATION_BETWEEN_TABLES:
        return (data as LckTableRowDataComplex).value as string
      case COLUMN_TYPE.LOOKED_UP_COLUMN:
        const originalColumn = getOriginalColumn(column)
        if (
          [
            COLUMN_TYPE.DATE,
            COLUMN_TYPE.DATETIME,
            COLUMN_TYPE.SINGLE_SELECT,
            COLUMN_TYPE.MULTI_SELECT
          ].includes(originalColumn.column_type_id)
        ) {
          return getColumnDisplayValue(
            originalColumn,
            (data as LckTableRowDataComplex).value,
            onlyBaseValue,
            i18nOptions
          )
        } else {
          return (data as LckTableRowDataComplex).value as string
        }
      case COLUMN_TYPE.VIRTUAL_LOOKED_UP_COLUMN:
        const virtualOriginalColumn = getOriginalColumn(column)
        if (
          virtualOriginalColumn.column_type_id !==
          COLUMN_TYPE.VIRTUAL_LOOKED_UP_COLUMN
        ) {
          return getColumnDisplayValue(
            virtualOriginalColumn,
            data,
            onlyBaseValue,
            i18nOptions
          )
        }
        localI18n
        return data as string
      case COLUMN_TYPE.MULTI_USER:
      case COLUMN_TYPE.MULTI_GROUP:
        return (data as LCKTableRowMultiDataComplex).value.join(', ')
      case COLUMN_TYPE.SINGLE_SELECT:
        const currentValue = column.settings.values?.[
          data as string
        ] as SelectValue
        if (onlyBaseValue) {
          return currentValue?.label
        }
        return {
          value: data as string,
          label: currentValue?.label,
          color: currentValue?.color,
          backgroundColor: currentValue?.backgroundColor
        }
      case COLUMN_TYPE.MULTI_SELECT:
        // Base value -> concatenation of the labels of the selected options
        if (onlyBaseValue) {
          if ((data as string[]).length > 0 && column.settings.values) {
            return (data as string[])
              .map((d) => column.settings.values![d]?.label)
              .join(', ')
          } else {
            return ''
          }
        }
        // Complex value -> an array of the configurations of the selected options
        if (column.settings.values) {
          return (data as string[]).reduce((options: SelectValue[], value) => {
            const currentOption = column.settings.values![value]
            if (currentOption) options.push(currentOption)
            return options
          }, [])
        }
        return []
      case COLUMN_TYPE.FORMULA:
        if (getColumnTypeId(column) === COLUMN_TYPE.DATE) {
          return (
            formatDate(
              data as Date,
              i18nOptions
                ? i18nOptions.dateFormat
                : localI18n.global.t('locokit.localePrime.dateFormat')
            ) || ''
          )
        } else {
          return data as string
        }
      case COLUMN_TYPE.DATE:
        return (
          formatDate(
            data as Date,
            i18nOptions
              ? i18nOptions.dateFormat
              : localI18n.global.t('locokit.localePrime.dateFormat')
          ) || ''
        )
      case COLUMN_TYPE.DATETIME:
        return (
          formatDate(
            data as Date,
            i18nOptions
              ? i18nOptions.dateTimeFormat
              : localI18n.global.t('locokit.localePrime.dateTimeFormat')
          ) || ''
        )
      case COLUMN_TYPE.TEXT:
        // parse markdown
        const dirty = marked(data as string, { async: false })
        // purify
        const clean = DOMPurify.sanitize(dirty, {
          USE_PROFILES: { html: true }
        })
        return clean
      default:
        return data as string
    }
  } catch (error) {
    console.error('Field with bad format', data, error)
    return ''
  }
}

// export function getDataFromTableViewColumn (
//   column: LckTableViewColumn,
//   data: LckTableRowData,
//   options: {
//     dateFormat: string | TranslateResult;
//     dateTimeFormat: string | TranslateResult;
//     noData: string | TranslateResult;
//     noReference: string | TranslateResult;
//   }): PopupContent['field'] {
//   switch (column.column_type_id) {
//     case COLUMN_TYPE.USER:
//     case COLUMN_TYPE.GROUP:
//     case COLUMN_TYPE.RELATION_BETWEEN_TABLES:
//       return {
//         label: column.text,
//         value: (data as LckTableRowDataComplex | null)?.value as string || options.noData as string,
//       }
//     case COLUMN_TYPE.LOOKED_UP_COLUMN:
//     case COLUMN_TYPE.VIRTUAL_LOOKED_UP_COLUMN:
//       const lookedUpColumnValue = getColumnDisplayValue(
//         column,
//         data,
//         true,
//         { dateFormat: options.dateFormat, dateTimeFormat: options.dateTimeFormat },
//       ) as string | number | boolean | undefined
//       return {
//         label: column.text,
//         value: lookedUpColumnValue != null && lookedUpColumnValue !== ''
//           ? lookedUpColumnValue
//           : options.noData as string,
//       }
//     case COLUMN_TYPE.MULTI_USER:
//       return {
//         label: column.text,
//         value: (data as LCKTableRowMultiDataComplex | null)?.value.join(', ') || options.noData as string,
//       }
//     case COLUMN_TYPE.SINGLE_SELECT:
//       return {
//         label: column.text,
//         value: (column.settings?.values as Record<string, SelectValue>)[data as string]?.label || options.noData as string,
//         color: (column.settings?.values as Record<string, SelectValue>)[data as string]?.color,
//         backgroundColor: (column.settings?.values as Record<string, SelectValue>)[data as string]?.backgroundColor,
//       }
//     case COLUMN_TYPE.MULTI_SELECT:
//       return {
//         label: column.text,
//         value: (data as string[]).length > 0 ? (data as string[]).map(d => (column.settings?.values as Record<string, SelectValue>)[d]?.label).join(', ') : options.noData as string,
//       }
//     case COLUMN_TYPE.FORMULA:
//       const value = getColumnTypeId(column) === COLUMN_TYPE.DATE
//         ? formatDate(data as Date, options.dateFormat)
//         : data

//       return {
//         label: column.text,
//         value: (value || options.noData) as string,
//       }
//     case COLUMN_TYPE.DATE:
//       // eslint-disable-next-line no-case-declarations
//       return {
//         label: column.text,
//         value: (data != null ? formatDate(data as Date, options.dateFormat) : options.noData) as string,
//       }
//     case COLUMN_TYPE.DATETIME:
//       // eslint-disable-next-line no-case-declarations
//       return {
//         label: column.text,
//         value: (data != null ? formatDate(data as Date, options.dateTimeFormat) : options.noData) as string,
//       }
//     default:
//       return { label: column.text, value: (data != null && data !== '' ? data : options.noData) as string }
//   }
// }

export function getFieldsEnhanced(
  fields: LckTableViewColumn[],
  record: Partial<LckTableRowOffline>
) {
  const fieldsEnhanced: Record<
    string,
    {
      column_type_id: COLUMN_TYPE
      text: string
      dropdownOptions?: {
        value: string
        label: string
        color: string
        backgroundColor: string
        position: number
      }[]
      rules: {
        required: boolean
        reference: boolean
      }
      defaultValue: any
      initialValue: any
      type?: string
      componentEditor: ReturnType<typeof getComponentEditorDetailForColumnType>
    }
  > = {}
  fields.forEach((f) => {
    /**
     * Compute initial values for vee-validate
     * built from the record provided
     *
     * need to be enhanced for more column_type ?
     */
    let initialValue = record?.data?.[f.id] ?? null
    if (!initialValue) {
      if (f.column_type_id === COLUMN_TYPE.DATETIME)
        initialValue = (initialValue as string).substring(0, 16)
    }
    // initialValues.value[f.id] = initialValue

    /**
     * Compute fieldsEnhanced
     */
    const componentEditor = getComponentEditorDetailForColumnType(
      f.column_type_id
    )

    fieldsEnhanced[f.id] = {
      column_type_id: f.column_type_id,
      text: f.text,
      rules: {
        required: f.required || f.validation?.required === true,
        reference: componentEditor === 'lck-autocomplete'
      },
      type: componentEditor === 'prime-checkbox' ? 'checkbox' : undefined,
      defaultValue:
        componentEditor === 'prime-checkbox' ? true : f.default?.value,
      initialValue,
      componentEditor
    }

    if (
      f.column_type_id === COLUMN_TYPE.SINGLE_SELECT ||
      f.column_type_id === COLUMN_TYPE.MULTI_SELECT
    ) {
      const values = f?.settings?.values
      if (values) {
        fieldsEnhanced[f.id].dropdownOptions = Object.keys(values)
          .map((key) => ({
            value: key,
            label: values[key].label,
            color: values[key].color,
            backgroundColor: values[key].backgroundColor,
            position: values[key].position || 0
          }))
          .sort(
            (firstValue, secondValue) =>
              firstValue.position - secondValue.position
          )
      }
    }
  })
  return fieldsEnhanced
}

export default {
  // getComponentDisplayCellForColumnType,
  // getComponentEditorCellForColumnType,
  // getComponentDisplayDetailForColumnType,
  getComponentEditorDetailForColumnType,
  isEditableColumn,
  getColumnTypeId,
  getOriginalColumn,
  // getDataFromTableViewColumn,
  // getColumnClass,
  getColumnDisplayValue
  // getColumnValidationRules,
  // GEOGRAPHIC_COLUMN_TYPES,
  // READ_ONLY_COLUMNS_TYPES,
}
